import { useCallback } from 'react';
import { StageGroup, StageInfo } from '../types/stageTypes';
import { prepareMappingsForSave, saveStagesMappings } from '../utils/stagePersistence';
import { groupStagesByType } from '../utils/stageGrouping';

/**
 * Custom hook for managing stage persistence operations
 * @param stageGroups Current stage groups
 * @param originalStages Original stage data
 * @param setOriginalStages Function to update original stages
 * @param setGroups Function to update stage groups
 * @param clearSelection Function to clear stage selections
 * @param setSavingState Function to update the saving state
 * @param fetchData Function to refresh data from the API
 * @returns Functions for saving and resetting stage data
 */
export const useStagePersistence = (
    stageGroups: StageGroup[],
    originalStages: StageInfo[],
    setOriginalStages: (stages: StageInfo[]) => void,
    setGroups: (groups: StageGroup[]) => void,
    clearSelection: () => void,
    setSavingState: (saving: boolean) => void,
    fetchData: () => Promise<void>
) => {
    // Save changes to the backend
    const handleSaveConfirm = useCallback(async () => {
        try {
            setSavingState(true);

            // Prepare mappings for save
            const mappingsToSave = prepareMappingsForSave(stageGroups);

            // Skip if no changes to save
            if (mappingsToSave.length === 0) {
                setSavingState(false);
                return;
            }

            // Save to backend
            await saveStagesMappings(mappingsToSave);

            // Refresh data from the API - this will correctly deduplicate stages
            // prioritizing ones with mappings due to the updated useStageData hook
            await fetchData();

            // Clear selection
            clearSelection();
        } catch (error) {
            console.error('Error in save flow:', error);
        } finally {
            setSavingState(false);
        }
    }, [stageGroups, setSavingState, fetchData, clearSelection]);

    // Reset all changes to original state
    const handleResetChanges = useCallback(() => {
        // Create a deep copy of the original stages
        const resetStages = originalStages.map(stage => ({
            ...stage,
            isChanged: false, // Reset the change flag
            mapping: { ...stage.mapping }
        }));

        // Update the original stages with reset state
        setOriginalStages(resetStages);

        // Generate fresh groups from the reset stages
        const resetGroups = groupStagesByType(resetStages);

        // Update groups state
        setGroups(resetGroups);

        // Clear selection
        clearSelection();
    }, [originalStages, setOriginalStages, setGroups, clearSelection]);

    return {
        handleSaveConfirm,
        handleResetChanges
    };
}; 